import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, Edit, X, ChefHat } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

interface ExtendedMenuDetails {
    id: string;
    menuItemId: string;
    preparationMethod: string | null;
    ingredientSources: string | null;
    pairingSuggestions: string | null;
    chefNotes: string | null;
    cookingTime: string | null;
    specialTechniques: string | null;
    createdAt: string;
    updatedAt: string;
}

interface MenuItemExtendedDetailsEditorProps {
    menuItemId: string;
    menuItemName: string;
    onClose?: () => void;
}

export function MenuItemExtendedDetailsEditor({
    menuItemId,
    menuItemName,
    onClose
}: MenuItemExtendedDetailsEditorProps) {
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [details, setDetails] = useState<ExtendedMenuDetails | null>(null);

    const [formData, setFormData] = useState({
        preparationMethod: "",
        ingredientSources: "",
        pairingSuggestions: "",
        chefNotes: "",
        cookingTime: "",
        specialTechniques: "",
    });

    useEffect(() => {
        fetchDetails();
    }, [menuItemId]);

    const fetchDetails = async () => {
        try {
            setIsLoading(true);
            const response = await apiRequest("GET", `/api/menu-items/${menuItemId}/extended-details`);

            // Check if response has content before parsing
            if (response.status !== 204) {
                const result = await response.json();

                // The backend returns { success, data, menuItem }
                // Extract the actual extended details from result.data
                const data = result.data;

                if (data) {
                    setDetails(data);
                    setFormData({
                        preparationMethod: data.preparationMethod || "",
                        ingredientSources: data.ingredientSources || "",
                        pairingSuggestions: data.pairingSuggestions || "",
                        chefNotes: data.chefNotes || "",
                        cookingTime: data.cookingTime || "",
                        specialTechniques: data.specialTechniques || "",
                    });
                } else {
                    // No details yet - enable editing mode
                    setIsEditing(true);
                }
            } else {
                setIsEditing(true);
            }

        } catch (error) {
            console.error("Error fetching extended details:", error);
            toast({
                title: "Error",
                description: "Failed to load menu details",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);
            await apiRequest("PUT", `/api/menu-items/${menuItemId}/extended-details`, formData);

            toast({
                title: "Success",
                description: "Menu details updated successfully!",
            });

            setIsEditing(false);
            await fetchDetails(); // Refresh the data
        } catch (error) {
            console.error("Error saving extended details:", error);
            toast({
                title: "Error",
                description: "Failed to save menu details",
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (details) {
            // Reset to original values
            setFormData({
                preparationMethod: details.preparationMethod || "",
                ingredientSources: details.ingredientSources || "",
                pairingSuggestions: details.pairingSuggestions || "",
                chefNotes: details.chefNotes || "",
                cookingTime: details.cookingTime || "",
                specialTechniques: details.specialTechniques || "",
            });
            setIsEditing(false);
        } else {
            // Close if no details exist
            onClose?.();
        }
    };

    if (isLoading) {
        return (
            <Card className="w-full">
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="w-full">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-primary/10 w-10 h-10 rounded-full flex items-center justify-center">
                            <ChefHat className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <CardTitle className="text-xl">Extended Menu Details</CardTitle>
                            <CardDescription>{menuItemName}</CardDescription>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isEditing && details && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setIsEditing(true)}
                                data-testid="button-edit-details"
                            >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                            </Button>
                        )}
                        {onClose && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                data-testid="button-close-details"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>
            </CardHeader>

            <CardContent className="space-y-6">
                {!details && !isEditing ? (
                    <div className="text-center py-8">
                        <p className="text-muted-foreground mb-4">
                            No extended details available yet. Use the AI Interview to add details or create them manually.
                        </p>
                        <Button onClick={() => setIsEditing(true)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Add Details Manually
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="preparationMethod">Preparation Method</Label>
                            {isEditing ? (
                                <Textarea
                                    id="preparationMethod"
                                    placeholder="Describe how this dish is prepared..."
                                    value={formData.preparationMethod}
                                    onChange={(e) => setFormData({ ...formData, preparationMethod: e.target.value })}
                                    rows={4}
                                    data-testid="input-preparation-method"
                                />
                            ) : (
                                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                    {formData.preparationMethod || "Not specified"}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="cookingTime">Cooking Time</Label>
                            {isEditing ? (
                                <Input
                                    id="cookingTime"
                                    placeholder="e.g., 15-20 minutes"
                                    value={formData.cookingTime}
                                    onChange={(e) => setFormData({ ...formData, cookingTime: e.target.value })}
                                    data-testid="input-cooking-time"
                                />
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    {formData.cookingTime || "Not specified"}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="ingredientSources">Ingredient Sources</Label>
                            {isEditing ? (
                                <Textarea
                                    id="ingredientSources"
                                    placeholder="Where do the ingredients come from? Farm-to-table details..."
                                    value={formData.ingredientSources}
                                    onChange={(e) => setFormData({ ...formData, ingredientSources: e.target.value })}
                                    rows={3}
                                    data-testid="input-ingredient-sources"
                                />
                            ) : (
                                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                    {formData.ingredientSources || "Not specified"}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="specialTechniques">Special Techniques</Label>
                            {isEditing ? (
                                <Textarea
                                    id="specialTechniques"
                                    placeholder="Any special cooking techniques? Sous-vide, char-grill, etc..."
                                    value={formData.specialTechniques}
                                    onChange={(e) => setFormData({ ...formData, specialTechniques: e.target.value })}
                                    rows={3}
                                    data-testid="input-special-techniques"
                                />
                            ) : (
                                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                    {formData.specialTechniques || "Not specified"}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="pairingSuggestions">Pairing Suggestions</Label>
                            {isEditing ? (
                                <Textarea
                                    id="pairingSuggestions"
                                    placeholder="What pairs well with this dish? Wines, sides, other menu items..."
                                    value={formData.pairingSuggestions}
                                    onChange={(e) => setFormData({ ...formData, pairingSuggestions: e.target.value })}
                                    rows={3}
                                    data-testid="input-pairing-suggestions"
                                />
                            ) : (
                                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                    {formData.pairingSuggestions || "Not specified"}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="chefNotes">Chef's Notes</Label>
                            {isEditing ? (
                                <Textarea
                                    id="chefNotes"
                                    placeholder="The story behind this dish, why you created it, what makes it special..."
                                    value={formData.chefNotes}
                                    onChange={(e) => setFormData({ ...formData, chefNotes: e.target.value })}
                                    rows={4}
                                    data-testid="input-chef-notes"
                                />
                            ) : (
                                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                    {formData.chefNotes || "Not specified"}
                                </p>
                            )}
                        </div>

                        {isEditing && (
                            <div className="flex gap-3 pt-4 border-t">
                                <Button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="flex-1"
                                    data-testid="button-save-details"
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="h-4 w-4 mr-2" />
                                            Save Changes
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                    data-testid="button-cancel-details"
                                >
                                    Cancel
                                </Button>
                            </div>
                        )}

                        {details && !isEditing && (
                            <div className="text-xs text-muted-foreground pt-4 border-t">
                                Last updated: {new Date(details.updatedAt).toLocaleString()}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
